import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { apiClient, unwrapApiResult, withAbortSignal } from "@/lib/api-client";

const STALE = 30_000;
const TIMEOUT = 15_000;

export function useHomepageBanners() {
  return useQuery({
    queryKey: ["homepage-banners"],
    queryFn: async ({ signal }) => {
      const result = await apiClient.query<any[]>({
        label: "homepage-banners",
        routeTag: "useHomepageBanners",
        timeoutMs: TIMEOUT,
        signal,
        run: (abortSignal) =>
          withAbortSignal(
            supabase.from("homepage_banners").select("*").eq("is_active", true).order("sort_order"),
            abortSignal
          ),
      });

      return unwrapApiResult(result) ?? [];
    },
    staleTime: STALE,
    retry: 2,
    retryDelay: 1000,
  });
}

export function useAllHomepageBanners() {
  return useQuery({
    queryKey: ["homepage-banners", "all"],
    queryFn: async ({ signal }) => {
      const result = await apiClient.query<any[]>({
        label: "all-homepage-banners",
        routeTag: "useAllHomepageBanners",
        timeoutMs: TIMEOUT,
        signal,
        run: (abortSignal) =>
          withAbortSignal(supabase.from("homepage_banners").select("*").order("sort_order"), abortSignal),
      });

      return unwrapApiResult(result) ?? [];
    },
    staleTime: STALE,
    retry: 2,
    retryDelay: 1000,
  });
}

export function useHomepageArticles() {
  return useQuery({
    queryKey: ["homepage-articles"],
    queryFn: async ({ signal }) => {
      const result = await apiClient.query<any[]>({
        label: "homepage-articles",
        routeTag: "useHomepageArticles",
        timeoutMs: TIMEOUT,
        signal,
        run: (abortSignal) =>
          withAbortSignal(
            supabase.from("homepage_articles").select("*").eq("is_active", true).order("sort_order"),
            abortSignal
          ),
      });

      return unwrapApiResult(result) ?? [];
    },
    staleTime: STALE,
    retry: 2,
    retryDelay: 1000,
  });
}

export function useAllHomepageArticles() {
  return useQuery({
    queryKey: ["homepage-articles", "all"],
    queryFn: async ({ signal }) => {
      const result = await apiClient.query<any[]>({
        label: "all-homepage-articles",
        routeTag: "useAllHomepageArticles",
        timeoutMs: TIMEOUT,
        signal,
        run: (abortSignal) =>
          withAbortSignal(supabase.from("homepage_articles").select("*").order("sort_order"), abortSignal),
      });

      return unwrapApiResult(result) ?? [];
    },
    staleTime: STALE,
    retry: 2,
    retryDelay: 1000,
  });
}

export function useTestimonials() {
  return useQuery({
    queryKey: ["testimonials"],
    queryFn: async ({ signal }) => {
      const result = await apiClient.query<any[]>({
        label: "testimonials",
        routeTag: "useTestimonials",
        timeoutMs: TIMEOUT,
        signal,
        run: (abortSignal) =>
          withAbortSignal(
            supabase.from("testimonials").select("*").eq("is_active", true).order("created_at", { ascending: false }).limit(3),
            abortSignal
          ),
      });

      return unwrapApiResult(result) ?? [];
    },
    staleTime: STALE,
    retry: 2,
    retryDelay: 1000,
  });
}

export function useAllTestimonials() {
  return useQuery({
    queryKey: ["testimonials", "all"],
    queryFn: async ({ signal }) => {
      const result = await apiClient.query<any[]>({
        label: "all-testimonials",
        routeTag: "useAllTestimonials",
        timeoutMs: TIMEOUT,
        signal,
        run: (abortSignal) =>
          withAbortSignal(
            supabase.from("testimonials").select("*").order("created_at", { ascending: false }),
            abortSignal
          ),
      });

      return unwrapApiResult(result) ?? [];
    },
    staleTime: STALE,
    retry: 2,
    retryDelay: 1000,
  });
}

export function useNewestProducts(limit = 5) {
  return useQuery({
    queryKey: ["newest-products", limit],
    queryFn: async ({ signal }) => {
      const result = await apiClient.query<any[]>({
        label: "newest-products",
        routeTag: "useNewestProducts",
        timeoutMs: TIMEOUT,
        signal,
        run: (abortSignal) =>
          withAbortSignal(
            supabase
              .from("products")
              .select("id,name,slug,brand,price,compare_at_price,currency,stock,stock_spain,status,category_id,description,specs,product_type,same_day_delivery,availability_source,show_on_sale")
              .eq("status", "active")
              .order("created_at", { ascending: false })
              .limit(limit),
            abortSignal
          ),
      });

      return unwrapApiResult(result) ?? [];
    },
    staleTime: STALE,
    retry: 2,
    retryDelay: 1000,
  });
}
